import React, { useState } from "react";
import { X, AlertCircle } from "lucide-react";
import { Button } from "./Button";
import { Card } from "./card";

interface ContractClosingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseContract: (closeReason: string) => void;
  contractTitle?: string;
  contractId?: string;
  contract?: any; // Contract object
  loading?: boolean;
}

const ContractClosingModal: React.FC<ContractClosingModalProps> = ({
  isOpen,
  onClose,
  onCloseContract,
  contractTitle = "Contract",
  contractId,
  contract,
  loading = false
}) => {
  const [closeReason, setCloseReason] = useState("");

  const handleClose = () => {
    if (closeReason.trim()) {
      onCloseContract(closeReason.trim());
    }
  };

  const handleCloseModal = () => {
    setCloseReason("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-full">
                <X className="h-6 w-6 text-gray-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Close Contract
                </h2>
                <p className="text-sm text-gray-600">
                  {contractTitle}
                </p>
              </div>
            </div>
            <button
              onClick={handleCloseModal}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              disabled={loading}
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {/* Information Text */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-yellow-900 mb-1">
                    Contract Closure
                  </h3>
                  <p className="text-sm text-yellow-800">
                    Please provide a reason for closing this contract. This action will end the contract and move it to closed status.
                  </p>
                </div>
              </div>
            </div>

            {/* Close Reason Text Area */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Close Reason *
              </label>
              <textarea
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                placeholder="Please describe why you are closing this contract..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows={4}
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum 10 characters required
              </p>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex gap-3 mt-6 pt-4 border-t">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleCloseModal}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-gray-600 hover:bg-gray-700"
              onClick={handleClose}
              disabled={loading || closeReason.trim().length < 10}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Closing...
                </div>
              ) : (
                "Close Contract"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractClosingModal;
